/**
 * Reaction catalogue for Team Chat.
 *
 * Stream stores a reaction as a `type` string. The first five emoji keep the
 * types the web and mobile apps have always used (like/love/haha/wow/sad), so
 * old reactions and the mobile app's picker stay in step; every other emoji
 * uses its slug. The emoji itself also rides on the reaction (`emoji`), so a
 * client that doesn't know a slug can still draw it.
 */

const E = (char, slug, name, cat, keywords = '') => ({ char, slug, name, cat, keywords });

export const EMOJI = [
  // Smileys & people
  E('😀', 'grinning', 'Grinning', 'Smileys & people', 'happy smile'),
  E('😊', 'blush', 'Smiling', 'Smileys & people', 'happy'),
  E('🙂', 'slight_smile', 'Slight smile', 'Smileys & people'),
  E('😉', 'wink', 'Wink', 'Smileys & people'),
  E('😂', 'haha', 'Laughing', 'Smileys & people', 'joy lol funny'),
  E('😅', 'sweat_smile', 'Sweat smile', 'Smileys & people', 'phew'),
  E('🤣', 'rofl', 'Rolling on the floor', 'Smileys & people', 'lol'),
  E('😍', 'heart_eyes', 'Heart eyes', 'Smileys & people', 'love'),
  E('🤔', 'thinking', 'Thinking', 'Smileys & people', 'hmm'),
  E('😬', 'grimacing', 'Grimacing', 'Smileys & people', 'yikes'),
  E('😲', 'wow', 'Astonished', 'Smileys & people', 'wow surprised'),
  E('😮', 'open_mouth', 'Surprised', 'Smileys & people', 'oh'),
  E('😔', 'sad', 'Pensive', 'Smileys & people', 'sad'),
  E('😢', 'cry', 'Crying', 'Smileys & people', 'sad tear'),
  E('😤', 'triumph', 'Huffing', 'Smileys & people', 'frustrated'),
  E('😴', 'sleeping', 'Sleeping', 'Smileys & people', 'tired'),
  E('🙃', 'upside_down', 'Upside down', 'Smileys & people'),
  E('😎', 'sunglasses', 'Cool', 'Smileys & people'),
  E('🥳', 'partying', 'Partying', 'Smileys & people', 'celebrate'),
  E('🤯', 'mind_blown', 'Mind blown', 'Smileys & people'),
  E('👍', 'like', 'Thumbs up', 'Smileys & people', 'yes ok approve +1'),
  E('👎', 'thumbsdown', 'Thumbs down', 'Smileys & people', 'no -1'),
  E('👏', 'clap', 'Clapping', 'Smileys & people', 'applause bravo'),
  E('🙌', 'raised_hands', 'Raised hands', 'Smileys & people', 'hooray'),
  E('🙏', 'pray', 'Thank you', 'Smileys & people', 'thanks please'),
  E('🤝', 'handshake', 'Handshake', 'Smileys & people', 'deal'),
  E('💪', 'muscle', 'Strong', 'Smileys & people', 'flex'),
  E('👋', 'wave', 'Wave', 'Smileys & people', 'hello bye'),
  E('👀', 'eyes', 'Eyes', 'Smileys & people', 'looking watching'),
  E('🤷', 'shrug', 'Shrug', 'Smileys & people'),
  E('🫡', 'salute', 'Salute', 'Smileys & people', 'on it'),
  E('👌', 'ok_hand', 'OK', 'Smileys & people'),
  // Symbols
  E('❤️', 'love', 'Heart', 'Symbols', 'love'),
  E('✅', 'check', 'Done', 'Symbols', 'check yes complete'),
  E('☑️', 'ballot_check', 'Checked', 'Symbols'),
  E('❌', 'x', 'No', 'Symbols', 'cross wrong'),
  E('⚠️', 'warning', 'Warning', 'Symbols', 'caution'),
  E('❗', 'exclamation', 'Important', 'Symbols'),
  E('❓', 'question', 'Question', 'Symbols'),
  E('💯', 'hundred', '100', 'Symbols', 'perfect'),
  E('🔥', 'fire', 'Fire', 'Symbols', 'hot lit'),
  E('⭐', 'star', 'Star', 'Symbols'),
  E('✨', 'sparkles', 'Sparkles', 'Symbols', 'new'),
  E('🎉', 'tada', 'Celebrate', 'Symbols', 'party congrats'),
  E('🚀', 'rocket', 'Rocket', 'Symbols', 'launch'),
  E('➕', 'plus', 'Plus one', 'Symbols', '+1'),
  E('🔴', 'red_circle', 'Red', 'Symbols'),
  E('🟢', 'green_circle', 'Green', 'Symbols'),
  // Work & hotel
  E('📈', 'chart_up', 'Trending up', 'Work', 'growth up'),
  E('📉', 'chart_down', 'Trending down', 'Work', 'decline down'),
  E('📊', 'bar_chart', 'Chart', 'Work', 'report'),
  E('💰', 'money_bag', 'Money', 'Work', 'revenue cash'),
  E('💵', 'dollar', 'Dollar', 'Work', 'cash'),
  E('🧾', 'receipt', 'Receipt', 'Work', 'invoice'),
  E('📅', 'calendar', 'Calendar', 'Work', 'date'),
  E('⏰', 'alarm', 'Alarm', 'Work', 'time late'),
  E('📌', 'pin', 'Pin', 'Work'),
  E('📎', 'paperclip', 'Attachment', 'Work'),
  E('📝', 'memo', 'Note', 'Work', 'write'),
  E('📞', 'phone', 'Phone', 'Work', 'call'),
  E('🏨', 'hotel', 'Hotel', 'Work'),
  E('🛏️', 'bed', 'Bed', 'Work', 'room'),
  E('🧹', 'broom', 'Housekeeping', 'Work', 'clean'),
  E('🍽️', 'dining', 'Dining', 'Work', 'food restaurant'),
  E('☕', 'coffee', 'Coffee', 'Work'),
  E('🔧', 'wrench', 'Maintenance', 'Work', 'fix engineering'),
  E('🔑', 'key', 'Key', 'Work'),
  E('🚗', 'car', 'Car', 'Work', 'parking'),
];

export const QUICK_REACTIONS = ['👍', '✅', '👀', '❤️', '😂'];

const BY_CHAR = new Map(EMOJI.map((e) => [e.char, e]));
const BY_SLUG = new Map(EMOJI.map((e) => [e.slug, e]));

export const emojiInfo = (char) => BY_CHAR.get(char) || null;

/** The Stream reaction type to store for an emoji. */
export const typeForEmoji = (char) => BY_CHAR.get(char)?.slug || null;

/** The emoji to draw for a stored reaction type (falls back to the emoji carried on a reaction). */
export const emojiForType = (type, reactions = []) => {
  const known = BY_SLUG.get(type);
  if (known) return known.char;
  const carried = reactions.find((r) => r.type === type && r.emoji);
  return carried ? carried.emoji : null;
};

export const searchEmoji = (query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return EMOJI;
  return EMOJI.filter((e) => `${e.name} ${e.slug} ${e.keywords}`.toLowerCase().includes(q));
};

export const EMOJI_CATEGORIES = Array.from(new Set(EMOJI.map((e) => e.cat)));

/**
 * Reaction chips for a message: [{ type, emoji, count, mine, names[] }].
 * Counts come from reaction_groups (newer API) or reaction_counts; names from
 * the latest reactions Stream sends with the message (up to ten).
 */
export const summarizeReactions = (message, currentUserId) => {
  const groups = message?.reaction_groups || null;
  const counts = groups
    ? Object.fromEntries(Object.entries(groups).map(([type, g]) => [type, g?.count || 0]))
    : (message?.reaction_counts || {});
  const latest = message?.latest_reactions || [];
  const own = new Set((message?.own_reactions || [])
    .filter((r) => !currentUserId || (r.user_id || r.user?.id) === currentUserId)
    .map((r) => r.type));
  const firstAt = (type) => {
    const g = groups?.[type];
    return g?.first_reaction_at ? new Date(g.first_reaction_at).getTime() : 0;
  };
  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => {
      const emoji = emojiForType(type, [...latest, ...(message?.own_reactions || [])]);
      const names = latest
        .filter((r) => r.type === type)
        .map((r) => ((r.user_id || r.user?.id) === currentUserId ? 'You' : (r.user?.name || r.user_id || r.user?.id)))
        .filter(Boolean);
      return { type, emoji, count, mine: own.has(type), names: Array.from(new Set(names)), firstAt: firstAt(type) };
    })
    .filter((r) => r.emoji)
    .sort((a, b) => a.firstAt - b.firstAt);
};

/** "You, Maria Santos and 2 others" */
export const reactorSentence = ({ names, count }) => {
  const ordered = [...names].sort((a, b) => (a === 'You' ? -1 : b === 'You' ? 1 : 0));
  const shown = ordered.slice(0, 3);
  const rest = Math.max(0, count - shown.length);
  if (!shown.length) return `${count} ${count === 1 ? 'person' : 'people'}`;
  if (rest > 0) return `${shown.join(', ')} and ${rest} other${rest === 1 ? '' : 's'}`;
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
};

const RECENT_KEY = 'chatRecentEmoji';

export const recentEmoji = () => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((c) => BY_CHAR.has(c)).slice(0, 8) : [];
  } catch (_) {
    return [];
  }
};

export const rememberEmoji = (char) => {
  try {
    const next = [char, ...recentEmoji().filter((c) => c !== char)].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (_) { /* storage blocked — recents just don't persist */ }
};
